import { GetStaticPathsResult, GetStaticPropsContext, GetStaticPropsResult } from 'next';
import invariant from 'tiny-invariant';

import { getBlogPostsBySpecies } from '../../../src/api/contentful/blog/getBlogPostsBySpecies';
import { getSpeciesById } from '../../../src/api/contentful/blog/getSpeciesById';
import { getRutilusGraphqlClient } from '../../../src/api/graphql/getRutilusGraphqlClient';
import { queryRutilusStatic } from '../../../src/api/graphql/queryRutilusStatic';
import { NOT_FOUND_WITH_REVALIDATION, REVALIDATE } from '../../../src/constants/next';
import { reportMykissError } from '../../../src/helpers/errorHandling';
import { parseInternalId } from '../../../src/helpers/number';
import { redirectSpeciesWithInternalId } from '../../../src/helpers/redirects/redirectSpeciesWithInternalId';
import {
  GetBasicSpeciesInfoFromList,
  GetBasicSpeciesInfoFromListVariables,
  GetSpeciesTopBaits,
  GetSpeciesTopBaitsVariables,
  GetSingleSpecies,
  GetSingleSpeciesVariables,
} from '../../../src/interfaces/graphql';
import { IPageMetadata } from '../../../src/interfaces/nextjs';
import {
  getBasicSpeciesInfoFromList,
  getSingleSpecies,
  getSpeciesTopBaits,
} from '../../../src/queries/rutilus/Species';
import { isExternalId } from '../../../src/helpers/strings/isExternalId';
import { ISpeciesProps, Species } from '../../../src/views/Species/Species';
import { RECENT_CATCH_LIMIT_DAYS } from '../../../src/constants/constants';
import { getNodesFromConnection } from '../../../src/helpers/graphql';
import { getSpeciesStatesList } from '../../../src/components/FindUSSpecies/helpers/buildStateDataSpecies';
import TOP_SIX_SPECIES_BY_STATE from '../../../src/constants/species/top_6_species_by_us_states_24042024.json';
import { DEFAULT_POPULAR_SPECIES } from '../../../src/components/PopularSpeciesNearYou/constants';

type IParams = {
  speciesId: string;
  slug?: string[];
};

export async function getStaticProps({
  params,
}: GetStaticPropsContext<IParams>): Promise<GetStaticPropsResult<IPageMetadata & ISpeciesProps>> {
  invariant(params, 'Params not found');

  try {
    const client = getRutilusGraphqlClient({ serverMode: 'static' });

    // Redirect /species/:internalId => /species/:externalId/:slug
    const internalId = parseInternalId(params.speciesId);
    if (internalId !== null) {
      return redirectSpeciesWithInternalId(internalId);
    }

    if (!isExternalId(params.speciesId)) {
      return { notFound: true };
    }

    const getSingleSpeciesQuery = queryRutilusStatic<GetSingleSpecies, GetSingleSpeciesVariables>(
      client,
      {
        query: getSingleSpecies,
        variables: { id: params.speciesId, recentCatchLimitDays: RECENT_CATCH_LIMIT_DAYS },
      },
    );

    const getSpeciesTopBaitsQuery = queryRutilusStatic<
      GetSpeciesTopBaits,
      GetSpeciesTopBaitsVariables
    >(client, {
      query: getSpeciesTopBaits,
      variables: { speciesId: params.speciesId, first: 5 },
    });

    const getPopularSpeciesQuery = queryRutilusStatic<
      GetBasicSpeciesInfoFromList,
      GetBasicSpeciesInfoFromListVariables
    >(client, {
      query: getBasicSpeciesInfoFromList,
      variables: {
        externalIds: DEFAULT_POPULAR_SPECIES,
      },
    });

    const result = await getSpeciesById(params.speciesId);

    const [getSingleSpeciesResult, getSpeciesTopBaitsResult, getPopularSpeciesResult] =
      await Promise.all([getSingleSpeciesQuery, getSpeciesTopBaitsQuery, getPopularSpeciesQuery]);

    const popularSpecies = getNodesFromConnection(getPopularSpeciesResult.response?.data.species);

    const topBaits = getNodesFromConnection(
      getSpeciesTopBaitsResult.response?.data.topProductUnits,
    );

    const blogPostItems = result ? await getBlogPostsBySpecies(1, 3, result.sys.id) : null;
    const blogPostSlug = result?.fields.slug;

    const speciesStatesList = getSpeciesStatesList(TOP_SIX_SPECIES_BY_STATE, params.speciesId);

    if (!getSingleSpeciesResult.response) {
      return {
        redirect: {
          destination: '/discover/species',
          permanent: true,
        },
      };
    }

    return {
      props: {
        _page: 'species',
        _pageTitle: 'Species',
        _pageId: params.speciesId,
        _pageSubject: getSingleSpeciesResult.response.data.entry.displayName,
        species: getSingleSpeciesResult.response.data.entry,
        topBaits: topBaits,
        speciesStatesList: speciesStatesList,
        popularSpecies: popularSpecies,
        blogPostItems: blogPostItems?.items ?? null,
        blogPostSlug: blogPostSlug ?? null,
      },
      revalidate: REVALIDATE.FOUR_HOURS,
    };
  } catch (error) {
    reportMykissError(error);
    return NOT_FOUND_WITH_REVALIDATION;
  }
}

export async function getStaticPaths(): Promise<GetStaticPathsResult<IParams>> {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export default Species;
